import { bucketMemberStatus, effectiveMemberStatusBucket, hasApplicationReviewHold, hasUnresolvedApplicantEvidence } from "./member-status";
export { hasUnresolvedApplicantEvidence } from "./member-status";

export type ApplicantOutcome = "rejected" | "canceled" | "expired";
/** App-owned, stay-scoped evidence. Raw harvest dates/status remain untouched. */
export interface MemberLifecycleEvidence {
  kind: "never_moved_in" | "needs_review";
  occupantId: string | null;
  outcome: ApplicantOutcome | null;
  source: string;
  recordedAt: string;
  evidence: string[];
  previous: { status: string | null; moveInDate: string | null; moveOutDate: string | null };
  decisionAt?: string | null;
  reviewedSubmissions?: Array<{ id: number; reviewedAt: string }>;
  reviewedAt?: string;
  supersededAt?: string;
  reopenedAt?: string;
  reopenPreviousDecision?: { decision: string | null; approvedAt: string | null; rejectedAt: string | null };
}

export interface LifecycleMember {
  status?: string | null;
  occupantId?: string | null;
  moveInDate?: Date | string | null;
  moveOutDate?: Date | string | null;
  bookingRequestRetiredAt?: Date | string | null;
  movedOutRetiredAt?: Date | string | null;
  applicantLifecycle?: MemberLifecycleEvidence | null;
}

/** Reject malformed calendar dates too (JS otherwise rolls February 30 into March). */
export function residencyDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return null;
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value) &&
      new Date(`${value.slice(0, 10)}T00:00:00Z`).toISOString().slice(0, 10) !== value.slice(0, 10)) return null;
  return date;
}

export function hasPositiveResidency(member: LifecycleMember, now = new Date()): boolean {
  if (hasUnresolvedApplicantEvidence(member)) return false;
  const bucket = bucketMemberStatus(member.status);
  const start = residencyDate(member.moveInDate);
  const end = residencyDate(member.moveOutDate);
  return (bucket === "active" || bucket === "moving_out" || bucket === "terminated") &&
    start !== null && start <= now && (member.moveOutDate == null || (end !== null && end >= start));
}

export function isArchivedApplicant(member: LifecycleMember, now = new Date()): boolean {
  const proof = member.applicantLifecycle;
  return proof?.kind === "never_moved_in" && !proof.supersededAt &&
    proof.occupantId === (member.occupantId ?? null) && !hasPositiveResidency(member, now);
}

/** Stay-scoped presentation only; do not change the occupancy status helper. */
export function lifecycleStatusBucket(member: LifecycleMember) {
  const proof = member.applicantLifecycle;
  if (proof && proof.occupantId !== (member.occupantId ?? null)) {
    return bucketMemberStatus(member.status);
  }
  return effectiveMemberStatusBucket(member);
}

export function memberLifecycle(member: LifecycleMember, now = new Date()) {
  const needsReview = hasApplicationReviewHold(member);
  const archived = isArchivedApplicant(member, now);
  const bucket = lifecycleStatusBucket(member);
  const rawBucket = bucketMemberStatus(member.status);
  const pending = rawBucket === "booking_request" || bucket === "moving_in";
  const start = residencyDate(member.moveInDate);
  const end = residencyDate(member.moveOutDate);
  const unoccupied = needsReview || archived || pending || (start !== null && start > now);
  const actualMoveInDate = unoccupied ? null : start;
  const actualMoveOutDate = unoccupied ? null : end;
  const tenureDays = !actualMoveInDate || (member.moveOutDate != null && !end) ||
      (end !== null && end < actualMoveInDate)
    ? null
    : Math.floor((Math.min(now.getTime(), end?.getTime() ?? now.getTime()) -
        actualMoveInDate.getTime()) / 86_400_000);
  const outcome = archived ? member.applicantLifecycle?.outcome ?? null : null;
  return {
    bucket,
    archivedApplicant: archived,
    needsReview,
    label: needsReview ? "Application — needs review" : archived ? "Never moved in" : bucket === "inactive" ? "Inactive" : member.status ?? "Unknown",
    outcome,
    actualMoveInDate,
    actualMoveOutDate,
    plannedMoveInDate: unoccupied ? start : null,
    tenureDays,
  };
}

export function formatMemberTenure(days: number | null): string {
  if (days === null || days < 0) return "—";
  if (days < 14) return `${days}d`;
  if (days < 60) return `${Math.floor(days / 7)}w`;
  const months = Math.floor(days / 30.44);
  if (months < 12) return `${months}mo`;
  const years = Math.floor(months / 12);
  return months % 12 ? `${years}y ${months % 12}mo` : `${years}y`;
}

export function memberVisible(member: LifecycleMember, showInactive: boolean, showArchived: boolean) {
  if (isArchivedApplicant(member)) return showArchived;
  return showInactive || lifecycleStatusBucket(member) !== "inactive";
}